<template>
    <div>
        <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mb-5"></v-progress-linear>

        <div class="mt-10" v-if="showShippingOptionForm">
            <v-card-title>
                <v-btn small class="mr-4" @click="closeShippingOptionForm">
                    <v-icon left>mdi-arrow-left</v-icon> Back
                </v-btn>

                {{ editLabel != "" ? 'Edit':'Add' }}  Shipping Option{{ editLabel != "" ? `: ${editLabel}` : '' }}
            </v-card-title>

            <EditShippingOption
                ref="editShippingOption"
                @post-save="fetchShippingOptions"
                @update-label="setEditLabel($event)"
                @close-form="closeShippingOptionForm"
                @update:selectedShippingOption="shippingOption = $event"
                :integrationId="id"
                :selectedShippingOption="shippingOption"
                :questionIds="questionIds"
            />
        </div>

        <div v-else>

            <v-card-title>

                <v-spacer></v-spacer>

                <v-btn small class="mr-2" @click="shareShippingOption" :disabled="isSharing">
                    <span v-if="isSharing">
                        <v-progress-circular
                            indeterminate
                            size="12"
                            width="2"
                            color="primary darken-3">
                        </v-progress-circular>
                        Generating, Please wait...
                    </span>
                    <span v-else>{{ shareBtnText }}</span>
                </v-btn>

                <v-btn
                    small class="mr-2"
                    v-if="shippingOptions.length <= 0"
                    @click="addDefaultShippingOptions"
                    :disabled="isSaving">
                        <span v-if="isSaving">
                            <v-progress-circular
                                indeterminate
                                size="12"
                                width="2"
                                color="primary darken-3">
                            </v-progress-circular>
                            Adding Default Shipping Options...
                        </span>
                        <span v-else>Add Default Shipping Options</span>
                </v-btn>

                <v-btn small color="primary" @click="toggleShippingOptionForm({integration_id: id})">
                    Add Shipping Option
                </v-btn>
            </v-card-title>

            <div v-if="selectedShippingOptions.length > 0" class="m-3">
                <v-btn
                    x-small
                    class="white--text"
                    color="#FF7033" @click="deleteItem(selectedShippingOptions)">
                    Delete Selected
                </v-btn>
                <v-btn
                    x-small
                    class="ml-1"
                    @click="duplicateItem(selectedShippingOptions)"
                    :disabled="isDuplicating"
                    >
                    Duplicate Selected
                </v-btn>
                <v-btn
                    v-if="adminOnly"
                    x-small
                    class="ml-1"
                    @click="copyItem(selectedShippingOptions)"
                    >
                    Copy Selected
                </v-btn>
            </div>

            <table class="table table-hover">
                <thead>
                    <tr>
                        <th width="40"></th>
                        <th v-if="shippingOptions.length > 1" width="40">
                            <v-checkbox
                                v-model="selectAll" @change="toggleAll"
                                class="ma-0 pa-0"
                                hide-details
                                dense
                                >
                            </v-checkbox>
                        </th>
                        <th width="80">ID</th>
                        <th width="250">Label</th>
                        <th width="300">Description</th>
                        <th>Conditions</th>
                        <th>Surcharges</th>
                        <th width="150">Status</th>
                        <th width="270">Actions</th>
                    </tr>
                </thead>
                <TableBody
                    :sortEndpoint="baseUrl+'/sort'"
                    :items="shippingOptions"
                    @reordered="shippingOptions = $event"
                    >
                    <template v-slot:default="{ item }">
                        <td style="cursor: move">
                            <v-icon>mdi-drag</v-icon>
                        </td>
                        <td v-if="shippingOptions.length > 1">
                            <v-checkbox
                                v-model="selectedShippingOptions"
                                :value="item.id"
                                class="ma-0 pa-0"
                                hide-details
                                dense
                                >
                            </v-checkbox>
                        </td>
                        <td>{{ item.id }}</td>
                        <td>{{ item.label }}</td>
                        <td>{{ item.description }}</td>
                        <td>
                            <div class="wrap-cell">
                                <small>{{ summarizeConditions(item.conditions, '  ', 200) }}</small>
                            </div>
                        </td>
                        <td>
                            <v-chip v-for="question in item.question_ids" :key="question.value"
                                class="ma-1"
                                small
                                :title="question.value"
                                >
                                {{ question.text }}
                            </v-chip>
                        </td>
                        <td>
                            <v-switch
                                @change="updateStatus(item)"
                                :disabled="isSaving"
                                :loading="isSaving && item.id === shippingOption.id"
                                v-model="item.enabled"
                                :label="item.enabled ? 'Enabled' : 'Disabled'"
                                hide-details
                                dense
                                class="mt-0 d-inline-block" />
                        </td>
                        <td>
                            <v-btn v-if="adminOnly" x-small @click="duplicateItem(item)" :disabled="isDuplicating">Duplicate</v-btn>
                            <v-btn v-if="adminOnly" x-small @click="copyItem(item)">Copy</v-btn>
                            <v-btn x-small @click="toggleShippingOptionForm(item)">Edit</v-btn>
                            <v-btn x-small class="white--text" color="#FF7033" @click="deleteItem(item)">Delete</v-btn>
                        </td>
                    </template>
                </TableBody>
            </table>

            <ConfirmationDialog
                action="delete"
                requestMethod="post"
                ref="deleteConfirmation"
                @post-confirm="postDelete"
            />

            <ConfirmationDialog
                    requestMethod="post"
                    ref="copyConfirmation"
                    :successMessage="`Shipping option${ selectedShippingOptions.length > 1 ? 's' : '' } copied successfully.`"
                >
                <template v-slot:title>
                    Copy Shipping Option{{ selectedShippingOptions.length > 1 ? 's' : '' }} to
                </template>
                <template v-slot:content="{ }">
                    <div>Please enter the Integration Id to copy the selected shipping option{{ selectedShippingOptions.length > 1 ? 's' : '' }} to:</div>
                    <v-text-field type="number" v-model="copyDestination" label="Integration ID" />
                </template>
            </ConfirmationDialog>
        </div>

    </div>
</template>

<script>
import { EventBus } from '@/js/event-bus.js'
import { ShippingOptionStruct, DEFAULT_SHIPPING_OPTIONS, DEFAULT_QUESTIONS } from './ShippingOptionStruct.js';
import EditShippingOption from './EditShippingOption';
import ConfirmationDialog from './components/ConfirmationDialog';
import TableBody from './components/TableBody';
import { isArray } from 'lodash';
import store from '@/js/store/index.js';

export default {
    props: {
        id: {
            type: Number,
            required: true
        }
    },

    components: {
        EditShippingOption,
        ConfirmationDialog,
        TableBody
    },

    data() {
        return {
            baseUrl: `/api/shipping-options/${this.id}`,
            shippingOptions: [],
            shippingOptionIndex: -1,
            shippingOption: new ShippingOptionStruct({}),
            showShippingOptionForm: false,
            confirmDeleteDialog: false,
            isLoading: true,
            isLoadingQuestions: true,
            isSaving: false,
            questionIds: null,

            shareBtnText: "Share Shipping Options",
            originalShareBtnText: null,
            isSharing: false,

            selectAll: false,
            selectedShippingOptions: [],

            copyDestination: '',
            editLabel: '',
            isDuplicating: false,
        };
    },

    created() {
        this.originalShareBtnText = this.shareBtnText;
        this.initialize();
    },

    computed: {

        adminOnly() {
            return this.$store.getters.isAdmin
        }

    },

    watch: {
        copyDestination(newVal) {
            if (newVal) {
                this.$refs.copyConfirmation.requestData.to_integration_id = newVal;
            }
        },


    },

    methods: {

        initialize() {
            this.fetchQuestions();
        },

        setEditLabel(label) {
            // limit the characters to 40, add ...
            if(label.length > 40) {
                label = label.substring(0, 40) + '...';
            }

            this.editLabel = label;
        },

        toggleAll() {
            if (this.selectAll) {
                this.selectedShippingOptions = this.shippingOptions.map(item => item.id);
            } else {
                this.selectedShippingOptions = [];
            }
        },

        fetchQuestions() {
            this.isLoadingQuestions = true;

            this.$http
                .get('/api/machship/questions/' + this.id)
                .then(({ data }) => {

                    this.questionIds = data.data.map(q => ({
                        value: q.id,
                        text: `[${q.id}] ` + q.question, //return the question text with id in brackets
                    }));

                    // append DEFAULT_QUESTIONS to questionIds and remove duplicates
                    const defaultQuestions = DEFAULT_QUESTIONS.filter(dq => !this.questionIds.some(q => q.value == dq.value));
                    defaultQuestions.map(dq => dq.text = `[${dq.value}] ` + dq.text);
                    this.questionIds = this.questionIds.concat(defaultQuestions);
                })
                .catch(err => {
                    // set the default
                    this.questionIds = DEFAULT_QUESTIONS;
                })
                .finally(() => {
                    this.isLoadingQuestions = false;
                    this.fetchShippingOptions();
                });
        },

        fetchShippingOptions() {
            this.isLoading = true;
            this.$http
                .get(this.baseUrl)
                .then(({ data }) => {
                    this.shippingOptions = (data.data ?? []).map(s => new ShippingOptionStruct(s, this.questionIds));
                })
                .catch(err => {
                    EventBus.$emit('show-error', err);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        deleteItem(item) {
            const itemsToDelete = [];
            if(isArray(item)) {
                this.$refs.deleteConfirmation.itemLabel = `${this.selectedShippingOptions.length} Selected Shipping Options`;
                itemsToDelete.push(...this.selectedShippingOptions);
            } else {
                itemsToDelete.push(item.id);
                this.$refs.deleteConfirmation.itemLabel = `${item.label}`;
            }

            this.$refs.deleteConfirmation.open(`${this.baseUrl}/delete`, {shipping_option_ids: itemsToDelete});
            this.deletedItems = itemsToDelete;
        },

        postDelete() {
            this.shippingOptions = this.shippingOptions.filter(c => !this.deletedItems.includes(c.id));
            this.selectedShippingOptions = [];
            this.deletedItems = [];
        },

        copyItem(item) {
            this.copyDestination = null;
            const itemsToCopy = [];
            if(isArray(item)) {
                itemsToCopy.push(...this.selectedShippingOptions);
            } else {
                itemsToCopy.push(item.id);
            }
            this.$refs.copyConfirmation.open(`${this.baseUrl}/copy`, {shipping_option_ids: itemsToCopy});
        },

        duplicateItem(item) {
            const itemsToDuplicate = isArray(item) ? [...this.selectedShippingOptions] : [item.id];
            this.isDuplicating = true;

            this.$http
                .withClientId(store.getters['LRSettingsState/getShopifyUserId'])
                .post(`${this.baseUrl}/copy`, {
                    shipping_option_ids: itemsToDuplicate,
                    to_integration_id: this.id
                })
                .then(() => {
                    EventBus.$emit('show-success', `Shipping option${itemsToDuplicate.length > 1 ? 's' : ''} duplicated successfully.`);
                    this.fetchShippingOptions();
                })
                .catch(err => {
                    EventBus.$emit('show-error', err);
                })
                .finally(() => {
                    this.isDuplicating = false;
                });
        },

        shareShippingOption() {
            this.isSharing = true;
            this.$http
                .get(this.baseUrl+'/share')
                .then(({ data }) => {

                    if (navigator.clipboard && navigator.clipboard.writeText) {

                        let copiedTxt = "Shipping option settings\n";

                        data.data.forEach(shippingOption => {
                            copiedTxt += `\n--------------------------------------------------------------\n`;
                            copiedTxt += `${shippingOption.label}\n`;

                            const questionLabels = (shippingOption.question_ids || []).map(id => {
                                const question = this.questionIds.find(q => q.value == id);
                                return question ? question.text : id;
                            }).join(', ');

                            copiedTxt += `Applied Surcharges: ${questionLabels}\n`;

                            if(shippingOption.conditions && shippingOption.conditions.length > 0) {
                                copiedTxt += `Conditions:\n` +
                                this.summarizeConditions(shippingOption.conditions) + "\n";
                            }

                            copiedTxt += `Carrier Settings:\n`;
                            copiedTxt += `  Return All Carriers Ungrouped: ${shippingOption.carriers_ungrouped ? 'Yes' : 'No'}\n`;
                            copiedTxt += `  Carriers And Services:\n`;
                            if(shippingOption.carriers && shippingOption.carriers.length > 0) {
                                shippingOption.carriers.forEach(carrier => {
                                    const includeTxt = carrier.exclude ? 'Exclude' : 'Include';
                                    copiedTxt += `    ${includeTxt}: ${carrier.carrier_name} - ${carrier.service_name}\n`;

                                    if(carrier.conditions && carrier.conditions.length > 0) {
                                        copiedTxt += `      Conditions:\n`;
                                        copiedTxt += this.summarizeConditions(carrier.conditions, '        ') + "\n";
                                    }
                                });
                            } else {
                                copiedTxt += "    All.\n";
                            }

                            copiedTxt += `\nPricing Rules:\n`;
                            const roundingMapping = {
                                0: 'No Rounding',
                                1: 'Round Up',
                                2: 'Round Down'
                            };
                            copiedTxt += `  Apply Rounding: ${roundingMapping[shippingOption.rounding]}\n`;

                            if(shippingOption.pricing_rules && shippingOption.pricing_rules.length > 0) {
                                copiedTxt += `  Rules:\n`;

                                shippingOption.pricing_rules.forEach(rule => {

                                    if(rule.enabled === false) {
                                        return;
                                    }

                                    if(rule.adjustment_value > 0){
                                        const adjustmentValue = rule.percentage_based === 1 ? `%${rule.adjustment_value}` : `${rule.adjustment_value}`;
                                        const ruleType = rule.type == 1 ? 'Discount' : 'Markup';
                                        copiedTxt += `    Apply ${adjustmentValue} ${ruleType}\n`;
                                    }

                                    if(rule.minimum_shipping_cost > 0) {
                                        copiedTxt += `    Minimum Shipping Cost: ${rule.minimum_shipping_cost}\n`;
                                    }
                                    if(rule.maximum_shipping_cost > 0) {
                                        copiedTxt += `    Maximum Shipping Cost: ${rule.maximum_shipping_cost}\n`;
                                    }

                                    if(rule.conditions && rule.conditions.length > 0) {
                                        copiedTxt += `    Conditions:\n`;
                                        copiedTxt += this.summarizeConditions(rule.conditions, '      ') + "\n";
                                    }
                                });
                            }

                        });

                        const content = copiedTxt.replace(/\t/g, '');
                        navigator.clipboard.writeText(content).catch((err) => {
                            console.error('Unable to copy text to clipboard', err);
                            EventBus.$emit('show-error', err);
                        });

                        this.shareBtnText = "Copied to clipboard";
                        setTimeout(() => {
                            this.shareBtnText = this.originalShareBtnText;
                        }, 2000); // reset button text after 2 seconds

                    } else {
                        EventBus.$emit('show-error', 'Clipboard API not supported in this environment.');
                    }

                })
                .catch(err => {
                    EventBus.$emit('show-error', err);
                })
                .finally(() => {
                    this.isSharing = false;
                });
        },

        filterConditions(groupedConditions) {
            if (!Array.isArray(groupedConditions)) return [];

            return groupedConditions
                .map(group =>
                    Array.isArray(group)
                        ? group
                            .filter(item =>
                                item &&
                                item.fieldName &&
                                item.operator
                            )
                        : []
                )
                .filter(group => group.length > 0)
                .map(group => [...group]) // reindex (like ->values())
        },

        summarizeConditions(conditions, spaces='  ', truncateLength = 0) {
            conditions = this.filterConditions(conditions);
            if(!isArray(conditions) || conditions.length === 0) {
                return '';
            }

            let summary = "";
            conditions.forEach((rules, index) => {
                let conditions = [];
                rules.forEach(rule => {
                    let fieldName = rule.fieldName
                    // if(fieldName == 'order-meta' || fieldName == 'product-meta') {
                    //     fieldName = rule.metaFieldName
                    // }
                    conditions.push(`${fieldName} ${rule.operator} "${rule.value}"`);
                });
                summary += spaces;
                summary += index > 0 ? `AND ` : `IF `;
                summary += `${conditions.join(" OR ")}\n`;
            });

            if (truncateLength > 0 && summary.length > truncateLength) {
                summary = summary.substring(0, truncateLength) + '...';
            }

            return summary;
        },

        toggleShippingOptionForm(item) {
            const index = item ? this.shippingOptions.indexOf(item) : -1;
            this.shippingOptionIndex = index;


            if(index < 0) {
                this.setEditLabel('');
                this.shippingOption = new ShippingOptionStruct(item);
                // increment the order: new shipping option will be added at the end
                this.shippingOption.order = this.shippingOptions.length ? Math.max(...this.shippingOptions.map(s => s.order ?? 0)) + 1 : 1;
            } else {
                this.shippingOption = item;
                this.setEditLabel(item.label);
            }

            this.questionIds = [...this.questionIds, ...this.shippingOption.question_ids];
            this.showShippingOptionForm = true
        },

        closeShippingOptionForm() {
            this.showShippingOptionForm = false;
            this.shippingOption = new ShippingOptionStruct({});
            this.shippingOptionIndex = -1;
            this.setEditLabel('');
        },

        updateStatus(item) {

            this.shippingOption = new ShippingOptionStruct(item);

            this.isSaving = true;

            const payload = {
                id: item.id,
                enabled: item.enabled,
                statusUpdate: true
            };

            const action = item.enabled ? 'enabled' : 'disabled';

            this.$http
                .withClientId(store.getters['LRSettingsState/getShopifyUserId'])
                .put(`${this.baseUrl}/${item.id}`, payload)
                .then(({ data }) => {
                    EventBus.$emit('show-success', `Shipping option ${action} successfully.`);
                    this.$emit('post-save');
                })
                .catch(err => {
                    EventBus.$emit('show-error', err);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        addDefaultShippingOptions() {
            this.isSaving = true;

            DEFAULT_SHIPPING_OPTIONS.map(option => {
                option.integration_id = this.id;
                return option;
            });

            this.$http
                .withClientId(store.getters['LRSettingsState/getShopifyUserId'])
                .post(`${this.baseUrl}`, DEFAULT_SHIPPING_OPTIONS)
                .then(({ data }) => {
                    EventBus.$emit('show-success', `Default shipping options added successfully.`);
                    this.fetchShippingOptions()
                })
                .catch(err => {
                    EventBus.$emit('show-error', err);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        }
    },
};
</script>


<style scoped lang="scss">
    .wrap-cell {
        white-space: normal !important;
        word-break: break-word;
        display: block; /* ensures wrapping inside table cell */
    }
</style>